import { useEffect, useState } from "react";
import "./FilterBar.css";
import { Dropdown, MultiSelectDropdown, getDefaultQuarter } from "./FilterBar";
import { getTimePeriods, getTerritories, getCompensationPivot } from "./api";

// Ported from core-compensation.component.ts's `maxTerritorySelection = 2`.
const MAX_TERRITORY_SELECTION = 2;

/**
 * RoleListTerritoryQuarterRow
 *
 * FIXED 2026-08-05 (3rd territory/quarter attempt - see FilterBar.jsx's
 * row2Content docstring for the full background). Old Angular's role-list
 * page (core-compensation.component.ts) and job-detail page
 * (non-core-compensation.component.ts, see JobDetailTerritoryQuarterRow.jsx)
 * are two SEPARATE component instances - only one is ever mounted
 * (recruitment-component.component.html's `@if(!hasStacks)`), so crossing
 * between them destroys one and constructs a fresh instance of the other.
 * Each owns its own Quarter + Territory state, starting empty/default every
 * time it's freshly constructed - there's no manual "reset" in old code
 * because there's nothing to reset; a new instance simply never had the old
 * value.
 *
 * This component is conditionally rendered by App.js based on
 * activeJobTitleId (mounted only in role-list mode), so React unmounts it
 * (destroying its state) the moment a Job Title resolves, and mounts a
 * fresh instance again on the way back - its useState initial values ARE
 * the reset, not a delayed correction from an effect.
 *
 * IMPORTANT - what does NOT reset this component: verticalStackId/subStackId
 * are real @Input()s on core-compensation.component.ts (bound from
 * recruitment-component.component.html), which stays MOUNTED across
 * Vertical/Sub Stack changes and Clear Core Stack while still in role-list
 * mode (none of those flip the `@if(!hasStacks)` this component swaps on) -
 * loadData() re-runs on the SAME instance via ngOnChanges()/
 * Config.stackModelChanged, and never touches `this.selectedTerritories`.
 * So this component must NOT be remounted (e.g. via a `key` prop) on those
 * changes - App.js only conditionally renders it on activeJobTitleId, which
 * already matches this exactly.
 *
 * Also owns the role-list data fetch - ported from
 * core-compensation.component.ts's own loadData()/getCompensationPivot call,
 * which old also does itself, inside the same page-owned instance (its
 * Promise.all also independently fetches its own timePeriod/territory
 * lists, matched here by fetching them fresh on every mount too). Confirmed
 * getCompensationPivot takes no territory param (compensation.service.ts) -
 * territoryIds here only drives RoleListTable's column grouping, reported
 * up via onTerritoryIdsChange (a display-only prop - no fetch depends on
 * it).
 *
 * CORRECTED: this comment used to claim "no race to guard here" for the
 * data fetch below - that was wrong. Rapid Sub Stack changes (Clear Core
 * Stack, the Sub Stack X button) can fire overlapping getCompensationPivot()
 * calls, and without a cancellation guard a stale response resolving after
 * a newer one would silently overwrite correct data - intermittent, timing
 * -dependent, reported as "role list sometimes goes blank, ~3x/day,
 * unreproducible, never with DevTools open" (DevTools overhead changes
 * response ordering enough to mask it). Now guarded the same way
 * ComparisonChart.jsx/JobDetailTerritoryQuarterRow.jsx already were.
 *
 * verticalStackId / subStackId: from App.js's filters.verticalStack.id /
 * the resolved sub-stack scoping id.
 * onDataChange(rows): raw pivot rows for the current Vertical/Quarter - App
 * .js applies the existing client-side sub-stack scoping itself, unchanged.
 * onTerritoryIdsChange(ids): for RoleListTable's grouped-columns view.
 */
export default function RoleListTerritoryQuarterRow({
  verticalStackId,
  subStackId,
  onDataChange,
  onTerritoryIdsChange,
}) {
  const [quarters, setQuarters] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [quarter, setQuarter] = useState(null);
  const [territoryIds, setTerritoryIds] = useState([]);

  // Matches old's own loadData() Promise.all, which fetches its own
  // timePeriod/territory lists rather than depending on a shared parent list.
  useEffect(() => {
    getTimePeriods()
      .then((periods) => {
        setQuarters(periods);
        setQuarter((prev) => prev ?? getDefaultQuarter(periods));
      })
      .catch(() => setQuarters([]));
    getTerritories().then(setTerritories).catch(() => setTerritories([]));
  }, []);

  useEffect(() => {
    onTerritoryIdsChange?.(territoryIds);
  }, [territoryIds, onTerritoryIdsChange]);

  // FIXED: was missing the cancellation guard every sibling fetch effect in
  // this codebase has (ComparisonChart.jsx, JobDetailTerritoryQuarterRow.jsx)
  // - rapid Sub Stack changes (Clear Core Stack, the Sub Stack X button) each
  // fire a new getCompensationPivot() call without cancelling the previous
  // one. If an older, now-stale request resolved AFTER a newer one, its
  // .then() would overwrite the correct data with stale (sometimes empty)
  // results - intermittent, timing-dependent, and exactly why it stopped
  // reproducing with DevTools open (the extra overhead changes response
  // ordering enough to avoid the race almost every time).
  useEffect(() => {
    // == null (not falsy-check): the "All" vertical is id 0, which must
    // still fetch - old Angular handled this explicitly (core-compensation's
    // `else if (this.verticalStackId === 0) await this.loadData()`), and the
    // pivot SQL's `OR :vertical_stack_id = 0` returns every role for it.
    if (verticalStackId == null) {
      onDataChange?.([]);
      return;
    }
    let cancelled = false;
    getCompensationPivot({
      verticalStackId,
      timePeriodId: quarter?.id,
      // FIXED 2026-08-20: was `isCore: subStackId == null`. Old always
      // passes isCore=TRUE on both calls - loadData() sends (…, this.isCore)
      // with isCore=true, and filterSidebarData() sends (…, true, true)
      // (core-compensation.component.ts:147 and :451). Sending is_core=false
      // returns the same rows (is_all=TRUE short-circuits that clause) but
      // is a different bind value, so Postgres can pick a different plan -
      // and the plan decides the order of rows tied on job_level.
      isCore: true,
      isAll: subStackId != null,
    })
      .then((rows) => {
        if (!cancelled) onDataChange?.(rows);
      })
      .catch(() => {
        if (!cancelled) onDataChange?.([]);
      });
    return () => {
      cancelled = true;
    };
  }, [verticalStackId, quarter, subStackId, onDataChange]);

  return (
    <div className="fb-row fb-row-static">
      <Dropdown
        label="Quarter"
        value={quarter}
        placeholder="Select quarter"
        options={quarters}
        getLabel={(o) => o.name || o.label}
        onChange={setQuarter}
      />
      <MultiSelectDropdown
        label="Territory"
        values={territoryIds}
        options={territories}
        getLabel={(o) => o.name}
        onChange={setTerritoryIds}
        placeholder="Select territory"
        maxSelection={MAX_TERRITORY_SELECTION}
      />
    </div>
  );
}
